import { useState, useEffect } from 'react';
import { Settings, ArrowLeft, Save, Trash2, Plus, Search, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useGetIdentity } from '@refinedev/core';

export default function AgentConfig() {
  const { data: identity } = useGetIdentity();
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [displayConfig, setDisplayConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);

  // Common field templates
  const fieldTemplates = {
    customer_name: { label: 'Customer Name', icon: '👤', type: 'text' },
    phone: { label: 'Phone', icon: '📞', type: 'phone' },
    phone_number: { label: 'Phone', icon: '📞', type: 'phone' },
    alternate_contact: { label: 'Alt Phone', icon: '📱', type: 'phone' },
    email: { label: 'Email', icon: '📧', type: 'email' },
    pincode: { label: 'Pincode', icon: '📍', type: 'text' },
    address: { label: 'Address', icon: '🏠', type: 'text' },
    product_interest: { label: 'Interest', icon: '🎯', type: 'text' },
    monthly_bill: { label: 'Monthly Bill', icon: '💰', type: 'currency' },
    sanctioned_load: { label: 'Load', icon: '⚡', type: 'text' },
    roof_type: { label: 'Roof Type', icon: '🏠', type: 'text' },
    available_space: { label: 'Space', icon: '📏', type: 'text' },
    call_disposition: { label: 'Status', icon: '📊', type: 'text' },
    time: { label: 'Call Time', icon: '⏰', type: 'datetime' },
    timestamp: { label: 'Timestamp', icon: '🕐', type: 'timestamp' },
    user_id: { label: 'User ID', icon: '👤', type: 'text' },
    extension: { label: 'Extension', icon: '📱', type: 'text' },
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get('/api/admin/workflows');
      console.log('Workflows response:', response.data);
      // Ensure it's an array
      if (Array.isArray(response.data)) {
        setWorkflows(response.data);
      } else {
        console.error('Workflows response is not an array:', response.data);
        setWorkflows([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setWorkflows([]);
      setLoading(false);
    }
  };

  const selectWorkflow = async (workflow) => {
    setSelectedWorkflow(workflow);
    setDetecting(true);
    
    try {
      // Fetch existing config
      const configResponse = await axios.get(`/api/display-config/workflows/${workflow.workflow_id}`);
      console.log('Config response:', configResponse.data);
      setDisplayConfig(Array.isArray(configResponse.data) ? configResponse.data : []);

      // Fetch available fields
      const fieldsResponse = await axios.get(`/api/display-config/workflows/${workflow.workflow_id}/available-fields`);
      console.log('Available fields response:', fieldsResponse.data);
      setAvailableFields(Array.isArray(fieldsResponse.data) ? fieldsResponse.data : []);
    } catch (error) {
      console.error('Error fetching config:', error);
      setDisplayConfig([]);
      setAvailableFields([]);
    } finally {
      setDetecting(false);
    }
  };

  const addField = (field) => {
    const fieldName = field.field_path.replace('gathered_context.', '');
    const template = fieldTemplates[fieldName] || {
      label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      icon: '📋',
      type: 'text'
    };

    const newField = {
      field_path: field.field_path,
      display_label: template.label,
      icon: template.icon,
      field_order: displayConfig.length + 1,
      show_in_list: true,
      field_type: template.type,
      format_pattern: null
    };

    setDisplayConfig([...displayConfig, newField]);
  };

  const removeField = (index) => {
    const newConfig = displayConfig.filter((_, i) => i !== index);
    // Reorder
    newConfig.forEach((field, i) => {
      field.field_order = i + 1;
    });
    setDisplayConfig(newConfig);
  };

  const moveField = (index, direction) => {
    const newConfig = [...displayConfig];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newConfig.length) return;
    
    [newConfig[index], newConfig[targetIndex]] = [newConfig[targetIndex], newConfig[index]];
    
    // Update order
    newConfig.forEach((field, i) => {
      field.field_order = i + 1;
    });
    
    setDisplayConfig(newConfig);
  };

  const updateField = (index, key, value) => {
    const newConfig = [...displayConfig];
    newConfig[index][key] = value;
    setDisplayConfig(newConfig);
  };

  const saveConfig = async () => {
    if (!selectedWorkflow) return;
    
    setSaving(true);
    try {
      await axios.put(`/api/display-config/workflows/${selectedWorkflow.workflow_id}`, {
        fields: displayConfig
      });
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const autoDetectAndApply = () => {
    // Auto-select top 5-6 most common fields
    const topFields = availableFields.slice(0, 6);
    const newConfig = topFields.map((field, index) => {
      const fieldName = field.field_path.replace('gathered_context.', '');
      const template = fieldTemplates[fieldName] || {
        label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        icon: '📋',
        type: 'text'
      };

      return {
        field_path: field.field_path,
        display_label: template.label,
        icon: template.icon,
        field_order: index + 1,
        show_in_list: true,
        field_type: template.type,
        format_pattern: null
      };
    });
    
    setDisplayConfig(newConfig);
  };

  if (identity?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Settings className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link
                to="/"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </Link>
              <div className="bg-indigo-600 p-2 rounded-lg">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Agent Display Configuration</h1>
                <p className="text-sm text-gray-500">Configure which fields to show for each agent</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Agent List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Agent</h2>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {workflows.map((workflow) => (
                    <button
                      key={workflow.id}
                      onClick={() => selectWorkflow(workflow)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        selectedWorkflow?.id === workflow.id
                          ? 'bg-indigo-100 border-2 border-indigo-600'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="font-medium text-gray-900">
                        {workflow.workflow_name || `Agent ${workflow.workflow_id}`}
                      </div>
                      <div className="text-sm text-gray-500">ID: {workflow.workflow_id}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Configuration */}
          <div className="lg:col-span-2">
            {!selectedWorkflow ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Agent Selected</h3>
                <p className="text-gray-500">Select an agent from the list to configure display fields</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current Configuration */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Display Fields</h2>
                    <div className="flex space-x-2">
                      <button
                        onClick={autoDetectAndApply}
                        disabled={detecting || availableFields.length === 0}
                        className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span>Auto-Configure</span>
                      </button>
                      <button
                        onClick={saveConfig}
                        disabled={saving || displayConfig.length === 0}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        <span>{saving ? 'Saving...' : 'Save'}</span>
                      </button>
                    </div>
                  </div>

                  {displayConfig.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No fields configured. Add fields from available fields below.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {displayConfig.map((field, index) => (
                        <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                          <div className="flex flex-col space-y-1">
                            <button
                              onClick={() => moveField(index, 'up')}
                              disabled={index === 0}
                              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => moveField(index, 'down')}
                              disabled={index === displayConfig.length - 1}
                              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              ▼
                            </button>
                          </div>
                          
                          <input
                            type="text"
                            value={field.icon}
                            onChange={(e) => updateField(index, 'icon', e.target.value)}
                            className="w-12 px-2 py-1 text-center border border-gray-300 rounded"
                            placeholder="📋"
                          />
                          
                          <input
                            type="text"
                            value={field.display_label}
                            onChange={(e) => updateField(index, 'display_label', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Label"
                          />
                          
                          <div className="text-xs text-gray-500 w-32 truncate">
                            {field.field_path.replace('gathered_context.', '')}
                          </div>
                          
                          <button
                            onClick={() => updateField(index, 'show_in_list', !field.show_in_list)}
                            className={`p-2 rounded ${field.show_in_list ? 'text-green-600' : 'text-gray-400'}`}
                            title={field.show_in_list ? 'Visible' : 'Hidden'}
                          >
                            {field.show_in_list ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          
                          <button
                            onClick={() => removeField(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Fields */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Fields</h2>
                  {detecting ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : availableFields.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No fields detected. Make sure this agent has received some calls.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {availableFields.map((field, index) => {
                        const fieldName = field.field_path.replace('gathered_context.', '');
                        const isAdded = displayConfig.some(f => f.field_path === field.field_path);
                        const template = fieldTemplates[fieldName] || { icon: '📋' };
                        
                        return (
                          <button
                            key={index}
                            onClick={() => !isAdded && addField(field)}
                            disabled={isAdded}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-left transition-colors ${
                              isAdded
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-900'
                            }`}
                          >
                            <span className="text-lg">{template.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{fieldName}</div>
                              <div className="text-xs text-gray-500">
                                {field.frequency} call{field.frequency !== 1 ? 's' : ''}
                              </div>
                            </div>
                            {!isAdded && <Plus className="w-4 h-4" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Preview */}
                {displayConfig.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm">
                          {displayConfig.filter(f => f.show_in_list).slice(0, 4).map((field, i) => (
                            <span key={i} className="inline-flex items-center space-x-1">
                              <span>{field.icon}</span>
                              <span className="text-gray-600">{field.display_label}:</span>
                              <span className="text-gray-900">Sample Value</span>
                              {i < Math.min(3, displayConfig.filter(f => f.show_in_list).length - 1) && <span className="text-gray-400">•</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
